import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Search, UserPlus } from 'lucide-react'
import type { Scope } from '../../core/types'
import { useAuth } from '../../core/auth'
import { ROLES, SCOPE_LABELS, canGrant, holdsRole, withRole, administrableScopes } from '../../core/caps'
import { useUsers, useDirectory, setCaps, seenLabel, toDate, type UserRow } from '../admin/model'
import { AddPerson, type Actor } from '../admin/Person'
import { Loading, Empty, ErrorState } from '../../ui/States'

/**
 * Flex PD staff and roles: who is Teacher, Principal, Learning coach and
 * District office, one building at a time (a grid of every role in every
 * building is a grid that gets clicked wrong). The same writes and rules as
 * Accounts: you hand out only what you may, and never change your own.
 * Bulk "give everyone shown the Teacher role" is one write per person, each
 * checked by the rules.
 */
const FPD_ROLES = ROLES.filter((r) => r.tool === 'flexpd')
const BUILDING_ROLES = FPD_ROLES.filter((r) => r.id !== 'flexpd.district')
const DISTRICT_ROLE = FPD_ROLES.find((r) => r.id === 'flexpd.district')!
const TEACHER = FPD_ROLES.find((r) => r.id === 'flexpd.teacher')!
type Tab = 'hs' | 'ms' | 'es' | 'district'

export function FpdStaff() {
  const { profile, user } = useAuth()
  const me = (user?.email ?? '').toLowerCase()
  const who = profile?.displayName ?? me
  const actor: Actor = { email: me, caps: profile?.caps, districtAdmin: profile?.districtAdmin, mayEditDirectory: false }
  const scopes = administrableScopes(actor)
  const tabs = (['hs', 'ms', 'es', 'district'] as Tab[]).filter((t) => scopes.includes(t) || scopes.includes('district'))
  const { rows, err } = useUsers(tabs.length > 0)
  const { rows: directory } = useDirectory()
  const [tab, setTab] = useState<Tab>(tabs[0] ?? 'hs')
  const [show, setShow] = useState<'here' | 'none' | 'all'>('here')
  const [q, setQ] = useState('')
  const [busy, setBusy] = useState<string | null>(null)
  const [msg, setMsg] = useState<string | null>(null)
  const [confirm, setConfirm] = useState(false)
  const [adding, setAdding] = useState(false)

  const dirBuildings = useMemo(() => new Map((directory ?? []).map((d) => [d.email.toLowerCase(), d.buildings ?? []])), [directory])
  if (!tabs.length) return <Empty title="Roles are given by permission managers" body="Ask a district admin, or whoever manages permissions in your building." />
  if (err) return <ErrorState body={`Could not load staff. ${err}`} />
  if (!rows) return <Loading label="Loading staff" />
  if (adding) return <AddPerson scopes={[tab]} actor={actor} who={who} existing={rows} onDone={() => setAdding(false)} />

  const roles = tab === 'district' ? [DISTRICT_ROLE] : BUILDING_ROLES
  const scope = tab as Scope
  const buildingsOf = (r: UserRow) => [...new Set([...(r.buildings ?? []), ...(dirBuildings.get(r.id) ?? [])])] as Scope[]
  const hasAny = (r: UserRow) => (r.caps ?? []).some((c) => c.startsWith('flexpd.'))
  const inHere = (r: UserRow) => tab === 'district' || buildingsOf(r).includes(scope) || roles.some((ro) => holdsRole(r.caps, ro, scope))
  const shown = rows
    .filter((r) => r.active !== false && !r.id.match(/^[0-9]/))
    .filter((r) => show === 'all' || (show === 'none' ? inHere(r) && !hasAny(r) : inHere(r)))
    .filter((r) => !q || `${r.displayName} ${r.email}`.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => a.displayName.localeCompare(b.displayName))
  const may = (role: (typeof FPD_ROLES)[number]) => role.caps.every((c) => canGrant(actor, c, scope))
  // Learning coach is counted without the principals who include it, or the totals double up.
  const principal = FPD_ROLES.find((x) => x.id === 'flexpd.principal')!
  const count = (role: (typeof FPD_ROLES)[number]) => rows.filter((r) => r.active !== false && holdsRole(r.caps, role, scope)
    && !(role.id === 'flexpd.coach' && holdsRole(r.caps, principal, scope))).length

  const toggle = async (r: UserRow, role: (typeof FPD_ROLES)[number], on: boolean) => {
    setBusy(r.id + role.id); setMsg(null)
    try { await setCaps(r.id, withRole(r.caps, role, scope, !on), who) }
    catch (e) { setMsg(`${r.displayName} was not changed. ${e instanceof Error ? e.message : String(e)}`) }
    finally { setBusy(null) }
  }
  const missingTeacher = shown.filter((r) => r.id !== me && !r.districtAdmin && !holdsRole(r.caps, TEACHER, scope))
  const bulk = async () => {
    setConfirm(false); setBusy('bulk'); setMsg(null)
    const failed: string[] = []
    for (const r of missingTeacher) {
      try { await setCaps(r.id, withRole(r.caps, TEACHER, scope, true), who) } catch { failed.push(r.displayName) }
    }
    setBusy(null)
    setMsg(failed.length ? `Not changed: ${failed.join(', ')}.` : null)
  }

  const box = (r: UserRow, role: (typeof FPD_ROLES)[number]) => {
    const on = holdsRole(r.caps, role, scope)
    if (r.districtAdmin) return <span className="work__sub">all</span>
    // A box you may not tick is absent, never greyed: say what they hold instead.
    if (r.id === me || !may(role)) return on ? <span className="pill pill--good">Yes</span> : <span className="work__sub">–</span>
    // Learning coach inside Principal: held, and unticking it alone removes nothing.
    const within = role.id === 'flexpd.coach' && holdsRole(r.caps, FPD_ROLES.find((x) => x.id === 'flexpd.principal')!, scope)
    if (within) return <span className="work__sub" title="Included in Principal">incl.</span>
    return <input type="checkbox" checked={on} disabled={busy === r.id + role.id || busy === 'bulk'}
      aria-label={`${r.displayName}: ${role.label}, ${SCOPE_LABELS[scope]}`} title={role.summary} onChange={() => toggle(r, role, on)} />
  }

  return (
    <>
      <Link to="/flex-pd" className="backlink">← Flex PD</Link>
      <h1 className="page-title">Flex PD staff and roles</h1>
      <p className="page-sub">One building at a time. Ticking a box gives that role in this building and saves straight away.</p>

      <div className="chips" role="tablist" aria-label="Building" style={{ margin: 'var(--s-4) 0 var(--s-2)' }}>
        {tabs.map((t) => (
          <button key={t} role="tab" aria-selected={tab === t} className={`chip${tab === t ? ' is-on' : ''}`} onClick={() => { setTab(t); setConfirm(false) }}>
            {t === 'district' ? 'District office' : SCOPE_LABELS[t]}
          </button>
        ))}
      </div>
      <p className="ov__p">{roles.map((r) => `${count(r)} ${r.label}`).join(' · ')}</p>
      {msg && <ErrorState body={msg} />}

      <div className="fpd__filters">
        <label className="sel__search"><Search size={15} aria-hidden="true" />
          <input className="opt__input" placeholder="Search name or email" aria-label="Search staff" value={q} onChange={(e) => setQ(e.target.value)} />
        </label>
        <div className="chips" role="tablist" aria-label="Show">
          {tab !== 'district' && <button role="tab" aria-selected={show === 'here'} className={`chip${show === 'here' ? ' is-on' : ''}`} onClick={() => setShow('here')}>In {SCOPE_LABELS[scope]}</button>}
          {tab !== 'district' && <button role="tab" aria-selected={show === 'none'} className={`chip${show === 'none' ? ' is-on' : ''}`} onClick={() => setShow('none')}>No Flex PD role yet</button>}
          <button role="tab" aria-selected={show === 'all' || tab === 'district'} className={`chip${show === 'all' || tab === 'district' ? ' is-on' : ''}`} onClick={() => setShow('all')}>All staff</button>
        </div>
        <button className="btn btn--ghost" style={{ width: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6 }} onClick={() => setAdding(true)}>
          <UserPlus size={16} aria-hidden="true" /> Add someone
        </button>
      </div>

      {tab !== 'district' && may(TEACHER) && missingTeacher.length > 0 && (
        <div className="bulkbar">
          {!confirm ? (
            <>
              <span>{missingTeacher.length} {missingTeacher.length === 1 ? 'person shown does' : 'people shown do'} not have the Teacher role in {SCOPE_LABELS[scope]}.</span>
              <button className="btn" style={{ width: 'auto' }} disabled={busy === 'bulk'} onClick={() => setConfirm(true)}>{busy === 'bulk' ? 'Saving…' : 'Give them all the Teacher role'}</button>
            </>
          ) : (
            <>
              <span>Give the Flex PD Teacher role in {SCOPE_LABELS[scope]} to {missingTeacher.length} {missingTeacher.length === 1 ? 'person' : 'people'}? Check the list first: support staff who do not do Flex PD should not have it.</span>
              <button className="btn" style={{ width: 'auto' }} onClick={bulk}>Yes, give it</button>
              <button className="btn btn--ghost" style={{ width: 'auto' }} onClick={() => setConfirm(false)}>Cancel</button>
            </>
          )}
        </div>
      )}

      {shown.length === 0 ? <Empty title="Nobody here" body="Try All staff, or add someone." /> : (
        <>
          <table className="table">
            <thead><tr>
              <th>Name</th><th>Buildings</th>
              {roles.map((r) => <th key={r.id} className="num">{r.label}</th>)}
              <th>Last sign-in</th>
            </tr></thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.id}>
                  <td><div style={{ fontWeight: 610 }}>{r.displayName}</div><div className="work__sub">{r.email}{r.id === me ? ' · you' : ''}</div></td>
                  <td className="work__sub">{buildingsOf(r).filter((b) => b !== 'district').map((b) => SCOPE_LABELS[b]).join(', ') || '–'}</td>
                  {roles.map((role) => <td key={role.id} className="num">{box(r, role)}</td>)}
                  <td className={toDate(r.lastSeenAt) ? '' : 'is-muted'}>{seenLabel(r.lastSeenAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <ul className="work cards">
            {shown.map((r) => (
              <li key={r.id} className="work__row" style={{ display: 'block' }}>
                <div style={{ fontWeight: 610 }}>{r.displayName}</div>
                <div className="work__sub">{r.email}</div>
                <div className="chips" style={{ marginTop: 'var(--s-1)' }}>
                  {roles.map((role) => (
                    <label key={role.id} className="pick" style={{ minHeight: 0, padding: '4px 8px' }}>
                      {box(r, role)} <span className="pick__label">{role.label}</span>
                    </label>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </>
  )
}

/** Whether this person may hand out any Flex PD role anywhere: the rail link. */
export function useMayGiveFpdRoles(): boolean {
  const { profile, user } = useAuth()
  if (!profile) return false
  const actor = { caps: profile.caps, districtAdmin: profile.districtAdmin }
  void user
  return administrableScopes(actor).some((s) => FPD_ROLES.some((r) => r.caps.every((c) => canGrant(actor, c, s))))
}
